/**
 * StudentTimeline — Centralised activity timeline for one student.
 *
 *   <StudentTimeline studentId={s.id} compact={false} />
 *
 *   • Aggregates every comment / activity touching this student from:
 *        Audit Bookmarks · Bookmark Comments · Anomaly Tickets ·
 *        Ticket Comments/Activity · Batch Remarks · Payment Comments ·
 *        Direct Comments (via the composer below)
 *   • Composer at the top — MentionTextarea with @ typeahead.
 *   • Each event: source badge (clickable deep-link), author, time-ago,
 *     rendered text with @mention chips. Edit/Delete on own direct comments.
 */
import { useCallback, useEffect, useMemo, useState } from 'react';
import { Card } from './ui/card';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import {
  MessageSquarePlus, RefreshCw, Bookmark, Ticket as TicketIcon, GraduationCap,
  CreditCard, MessageCircle, ExternalLink, Edit2, Trash2, Save, X, Presentation,
} from 'lucide-react';
import { toast } from 'sonner';
import api from '../lib/api';
import MentionTextarea from './MentionTextarea';
import MentionText from './MentionText';

const SOURCE_META = {
  manual:           { icon: MessageCircle,    color: 'text-violet-600',  bg: 'bg-violet-50',  border: 'border-violet-200',  label: 'Comment' },
  payment:          { icon: CreditCard,       color: 'text-amber-600',   bg: 'bg-amber-50',   border: 'border-amber-200',   label: 'Payment' },
  bookmark:         { icon: Bookmark,         color: 'text-blue-600',    bg: 'bg-blue-50',    border: 'border-blue-200',    label: 'Bookmark' },
  bookmark_comment: { icon: Bookmark,         color: 'text-blue-500',    bg: 'bg-blue-50',    border: 'border-blue-100',    label: 'Bookmark' },
  ticket:           { icon: TicketIcon,       color: 'text-rose-600',    bg: 'bg-rose-50',    border: 'border-rose-200',    label: 'Ticket' },
  ticket_comment:   { icon: TicketIcon,       color: 'text-rose-500',    bg: 'bg-rose-50',    border: 'border-rose-100',    label: 'Ticket' },
  ticket_activity:  { icon: TicketIcon,       color: 'text-rose-500',    bg: 'bg-rose-50',    border: 'border-rose-100',    label: 'Ticket' },
  batch_remark:     { icon: GraduationCap,    color: 'text-emerald-600', bg: 'bg-emerald-50', border: 'border-emerald-200', label: 'Batch' },
  demo:             { icon: Presentation,      color: 'text-violet-700',  bg: 'bg-violet-50',  border: 'border-violet-200',  label: 'Demo' },
};

function timeAgo(iso) {
  if (!iso) return '';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  const diff = Math.floor((Date.now() - d.getTime()) / 1000);
  if (diff < 60) return 'just now';
  if (diff < 3600) return `${Math.floor(diff / 60)}m ago`;
  if (diff < 86400) return `${Math.floor(diff / 3600)}h ago`;
  if (diff < 604800) return `${Math.floor(diff / 86400)}d ago`;
  return d.toLocaleDateString('en', { day: '2-digit', month: 'short', year: '2-digit' });
}

function fullStamp(iso) {
  if (!iso) return '';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  return d.toLocaleString('en', { day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' });
}

export default function StudentTimeline({ studentId, compact = false }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [composerOpen, setComposerOpen] = useState(false);
  const [text, setText] = useState('');
  const [mentions, setMentions] = useState([]);
  const [posting, setPosting] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [editText, setEditText] = useState('');
  const [editMentions, setEditMentions] = useState([]);
  const [filter, setFilter] = useState('all');

  const load = useCallback(async () => {
    if (!studentId) return;
    setLoading(true);
    try {
      const r = await api.get(`/training/students/${studentId}/timeline`);
      setData(r.data);
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Failed to load timeline');
    } finally {
      setLoading(false);
    }
  }, [studentId]);

  useEffect(() => { load(); }, [load]);

  const events = useMemo(() => {
    if (!data?.events) return [];
    if (filter === 'all') return data.events;
    return data.events.filter(e => {
      if (filter === 'comments') return ['manual', 'bookmark_comment', 'ticket_comment'].includes(e.source);
      if (filter === 'system') return ['bookmark', 'ticket', 'ticket_activity', 'batch_remark', 'payment', 'demo'].includes(e.source);
      return e.source === filter;
    });
  }, [data, filter]);

  const post = async () => {
    if (!text.trim()) return;
    setPosting(true);
    try {
      await api.post(`/training/students/${studentId}/comments`, {
        text: text.trim(),
        mentioned_user_ids: mentions,
      });
      toast.success('Comment posted');
      setText('');
      setMentions([]);
      setComposerOpen(false);
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Failed to post');
    } finally {
      setPosting(false);
    }
  };

  const startEdit = (ev) => {
    setEditingId(ev.id);
    setEditText(ev.text || '');
    setEditMentions(ev.mentioned_user_ids || []);
  };

  const saveEdit = async (ev) => {
    const realId = ev.id.replace(/^manual-/, '');
    try {
      await api.put(`/training/students/${studentId}/comments/${realId}`, {
        text: editText.trim(),
        mentioned_user_ids: editMentions,
      });
      toast.success('Comment updated');
      setEditingId(null);
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Update failed');
    }
  };

  const remove = async (ev) => {
    if (!window.confirm('Delete this comment?')) return;
    const realId = ev.id.replace(/^manual-/, '');
    try {
      await api.delete(`/training/students/${studentId}/comments/${realId}`);
      toast.success('Comment deleted');
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Delete failed');
    }
  };

  const summary = data?.sources_summary || {};
  const totalEvents = data?.count || 0;

  return (
    <div className="space-y-2" data-testid="student-timeline">
      {/* Header */}
      <div className="flex items-center justify-between gap-2 flex-wrap">
        <div className="flex items-center gap-2">
          <p className="text-[10px] uppercase tracking-wider text-slate-400 font-semibold flex items-center gap-1">
            <MessageCircle className="w-3 h-3" /> Activity Timeline ({totalEvents})
          </p>
          {!compact && totalEvents > 0 && (
            <div className="flex gap-0.5">
              {['all', 'comments', 'system'].map(k => (
                <button key={k} onClick={() => setFilter(k)}
                  className={`text-[10px] px-1.5 py-0.5 rounded ${filter === k ? 'bg-violet-600 text-white' : 'bg-slate-100 text-slate-500 hover:bg-slate-200'}`}
                  data-testid={`student-timeline-filter-${k}`}>
                  {k.charAt(0).toUpperCase() + k.slice(1)}
                </button>
              ))}
            </div>
          )}
        </div>
        <div className="flex items-center gap-1">
          <button onClick={load} title="Refresh" className="p-1 text-slate-400 hover:text-slate-600 rounded hover:bg-slate-100" data-testid="student-timeline-refresh">
            <RefreshCw className={`w-3 h-3 ${loading ? 'animate-spin' : ''}`} />
          </button>
          <Button size="sm" variant={composerOpen ? 'outline' : 'default'}
            className={composerOpen ? 'h-7 text-[10px]' : 'h-7 text-[10px] bg-violet-600 hover:bg-violet-700'}
            onClick={() => setComposerOpen(o => !o)}
            data-testid="student-timeline-add">
            {composerOpen ? <><X className="w-3 h-3 mr-1" /> Cancel</> : <><MessageSquarePlus className="w-3 h-3 mr-1" /> Add Comment</>}
          </Button>
        </div>
      </div>

      {/* Source mini-stats (chips) */}
      {!compact && totalEvents > 0 && (
        <div className="flex flex-wrap gap-1">
          {Object.entries(summary).map(([s, n]) => {
            const meta = SOURCE_META[s] || {};
            const Icon = meta.icon || MessageCircle;
            return (
              <span key={s} className={`inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-[9px] ${meta.bg} ${meta.color} border ${meta.border}`} title={s}>
                <Icon className="w-2.5 h-2.5" /> {meta.label || s}: <strong>{n}</strong>
              </span>
            );
          })}
        </div>
      )}

      {/* Composer */}
      {composerOpen && (
        <Card className="p-2 border-violet-200 bg-violet-50/40">
          <MentionTextarea
            value={text}
            onChange={setText}
            onMentionsChange={setMentions}
            placeholder="Write a comment… use @ to mention a teammate"
            rows={3}
            data-testid="student-timeline-composer"
          />
          <div className="flex justify-end gap-1 mt-1.5">
            <Button size="sm" variant="ghost" className="h-7 text-[10px]" onClick={() => { setText(''); setMentions([]); setComposerOpen(false); }}>Cancel</Button>
            <Button size="sm" disabled={!text.trim() || posting} onClick={post}
              className="h-7 text-[10px] bg-violet-600 hover:bg-violet-700"
              data-testid="student-timeline-post">
              {posting ? 'Posting…' : 'Post'}
            </Button>
          </div>
        </Card>
      )}

      {/* Timeline */}
      <div className="space-y-1.5">
        {loading && !data && (
          <p className="text-[11px] text-slate-400 italic text-center py-4">Loading timeline…</p>
        )}
        {data && events.length === 0 && (
          <p className="text-[11px] text-slate-400 italic text-center py-4">
            No activity yet. Be the first to comment.
          </p>
        )}
        {events.map(ev => {
          const meta = SOURCE_META[ev.source] || SOURCE_META.manual;
          const Icon = meta.icon;
          const isEditing = editingId === ev.id;
          return (
            <div key={ev.id} className="flex gap-2 group" data-testid={`student-timeline-event-${ev.source}`}>
              <div className={`flex-shrink-0 w-7 h-7 rounded-full flex items-center justify-center ${meta.bg} ${meta.color} border ${meta.border}`}>
                <Icon className="w-3.5 h-3.5" />
              </div>
              <div className={`flex-1 min-w-0 p-2 rounded border ${meta.border} ${meta.bg.replace('50', '50/60')}`}>
                <div className="flex items-start justify-between gap-2 flex-wrap">
                  <div className="flex items-baseline gap-2 flex-wrap min-w-0">
                    <span className="text-[11px] font-semibold text-slate-700 truncate">{ev.author || 'Unknown'}</span>
                    <Badge variant="outline" className={`text-[9px] ${meta.color} ${meta.border} bg-white`}>
                      {ev.source_label}
                    </Badge>
                    {ev.extra?.status && (
                      <span className="text-[9px] text-slate-500 uppercase">· {ev.extra.status}</span>
                    )}
                    {ev.extra?.edited && (
                      <span className="text-[9px] text-slate-400 italic">edited</span>
                    )}
                  </div>
                  <div className="flex items-center gap-1 flex-shrink-0">
                    <span className="text-[10px] text-slate-400" title={fullStamp(ev.at)}>{timeAgo(ev.at)}</span>
                    {ev.source_link && (
                      <a href={ev.source_link} className="text-slate-400 hover:text-violet-600" title="Open in module">
                        <ExternalLink className="w-3 h-3" />
                      </a>
                    )}
                    {ev.can_edit && !isEditing && (
                      <>
                        <button onClick={() => startEdit(ev)} className="text-slate-400 hover:text-violet-600 opacity-0 group-hover:opacity-100" title="Edit">
                          <Edit2 className="w-3 h-3" />
                        </button>
                        <button onClick={() => remove(ev)} className="text-slate-400 hover:text-rose-600 opacity-0 group-hover:opacity-100" title="Delete">
                          <Trash2 className="w-3 h-3" />
                        </button>
                      </>
                    )}
                  </div>
                </div>
                {isEditing ? (
                  <div className="mt-1.5">
                    <MentionTextarea
                      value={editText}
                      onChange={setEditText}
                      onMentionsChange={setEditMentions}
                      rows={2}
                      data-testid="student-timeline-edit"
                    />
                    <div className="flex gap-1 justify-end mt-1">
                      <Button size="sm" variant="ghost" className="h-6 text-[10px]" onClick={() => setEditingId(null)}>Cancel</Button>
                      <Button size="sm" className="h-6 text-[10px] bg-violet-600 hover:bg-violet-700" onClick={() => saveEdit(ev)}>
                        <Save className="w-3 h-3 mr-1" /> Save
                      </Button>
                    </div>
                  </div>
                ) : (
                  <div className="text-xs text-slate-700 mt-1 leading-relaxed whitespace-pre-wrap break-words">
                    <MentionText text={ev.text} />
                  </div>
                )}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}
